import { ADS_MANAGEMENT_READ_TIMEOUT_MS, apiClient } from './apiClient';

/**
 * CORE-M-04 Campaign Review (Art's Q-6). Read-only against Google Ads; the
 * two marks (Raise with client, Snooze) are Core rows, never Google writes.
 *
 * ⛔ Branch on read_ok and tracking_quiet BEFORE the length of `flagged`.
 * An empty list only means "none need action" when the read worked and
 * tracking was fine; otherwise it means Core could not look.
 */

export type CampaignReviewPoorReason = 'no_conversions' | 'high_cpa';
export type CampaignReviewConversionRule = 'whatconverts' | 'bidding';
export type CampaignReviewTargetSource = 'campaign' | 'portfolio' | 'client';
export type CampaignReviewSnoozeReason = 'wrong' | 'not_now' | 'need_to_check';

export interface CampaignReviewWindow {
  days: number;
  spend_usd: number;
  clicks: number;
  qualified_conversions: number;
  cpa_usd: number | null;
  poor_reason: CampaignReviewPoorReason | null;
}

export interface CampaignReviewRow {
  campaign_resource_name: string;
  campaign_id: string;
  campaign_name: string;
  target_cpa_usd: number | null;
  target_source: CampaignReviewTargetSource | null;
  /** The campaign's own bid strategy type, e.g. MAXIMIZE_CLICKS. Shown when
   *  the client target is used, so the fallback is visible. */
  campaign_strategy?: string | null;
  /** The campaign uses a shared strategy Core could not read. */
  portfolio_unreadable?: boolean;
  conversion_rule: CampaignReviewConversionRule | null;
  windows: CampaignReviewWindow[];
  /** The numbers that tripped it, in plain words. */
  reason: string;
  raised: { by: string; at: string | null } | null;
  snooze?: {
    reason: CampaignReviewSnoozeReason | null;
    until: string | null;
    by: string;
    at: string | null;
    spend_at_snooze_usd: number | null;
  } | null;
}

export interface CampaignReviewNotChecked {
  brand?: number;
  no_target?: number;
  not_enough_history?: number;
  recent_change?: number;
  change_history_unavailable?: number;
}

export interface CampaignReviewResponse {
  tenant_id: string;
  customer_id: string;
  read_ok: boolean;
  message: string;
  as_of: string;
  windows: Record<string, { days: number; start: string; end: string }>;
  /** One plain line when tracking has an open problem; then nothing is flagged. */
  tracking_quiet: string | null;
  evaluated_count: number;
  flagged: CampaignReviewRow[];
  snoozed: CampaignReviewRow[];
  not_checked: CampaignReviewNotChecked;
  /** False until the marks table exists on this environment. */
  marks_available: boolean;
  thresholds: Record<string, number>;
}

export const campaignReviewQueryKey = (tenantId: string | null) =>
  ['ga-campaign-review', tenantId] as const;

export async function getCampaignReview(tenantId: string): Promise<CampaignReviewResponse> {
  const { data } = await apiClient.get<CampaignReviewResponse>(
    '/google-ads/campaign-review',
    { params: { tenant_id: tenantId }, timeout: ADS_MANAGEMENT_READ_TIMEOUT_MS },
  );
  return data;
}

export interface CampaignReviewMark {
  id: string;
  campaign_resource_name: string;
  kind: 'raised' | 'snooze';
  reason: CampaignReviewSnoozeReason | null;
  spend_at_mark_usd: number | null;
  expires_at: string | null;
  actor: string;
  created_at: string | null;
}

export async function raiseCampaignWithClient(
  tenantId: string,
  campaignResourceName: string,
): Promise<CampaignReviewMark> {
  const { data } = await apiClient.post<CampaignReviewMark>(
    '/google-ads/campaign-review/raise',
    { tenant_id: tenantId, campaign_resource_name: campaignResourceName },
  );
  return data;
}

/** The server reads the campaign's 30-day spend from Google itself; the
 *  browser does not send one. */
export async function snoozeCampaignReview(
  tenantId: string,
  campaignResourceName: string,
  reason: CampaignReviewSnoozeReason,
): Promise<CampaignReviewMark> {
  const { data } = await apiClient.post<CampaignReviewMark>(
    '/google-ads/campaign-review/snooze',
    { tenant_id: tenantId, campaign_resource_name: campaignResourceName, reason },
  );
  return data;
}
